import { createAuthController } from "./auth.js";
import { loadAvailability, submitCatalogueManifest } from "./availability.js";
import { groupBrowseRecords, indexCatalogue, loadCatalogue } from "./catalogue-store.js";
import { bindQueryControls, createCatalogueView } from "./catalogue-view.js";
import { createCollectionController } from "./collection.js";
import { createCollectionPage } from "./collection-page.js";
import { createFeedbackController } from "./feedback.js";
import { createBurgerMenu } from "./menu.js";
import { createRatingsController } from "./ratings.js";

async function startBrowseInterface({ document, ratings, collection }) {
  const error = document.querySelector("#catalogueError");
  const view = createCatalogueView({
    document,
    onMount(root) {
      ratings.mount(root).catch(() => {});
      collection.mount?.(root);
    },
  });
  const query = bindQueryControls({
    document,
    onChange: (next) => view.setQuery(next),
  });

  async function load() {
    error?.replaceChildren();
    try {
      const [catalogue, availability] = await Promise.all([
        loadCatalogue(),
        loadAvailability({ document }).catch(() => new Map()),
      ]);
      view.setAvailability(availability);
      view.setSections(groupBrowseRecords(catalogue));
      submitCatalogueManifest({ document, records: catalogue }).catch(() => {});
      return catalogue;
    } catch {
      if (error) {
        const message = document.createElement("p");
        message.textContent = "The bottle catalogue could not be loaded.";
        const retry = document.createElement("button");
        retry.type = "button";
        retry.textContent = "Retry";
        retry.addEventListener("click", load, { once: true });
        error.append(message, retry);
      }
      return [];
    }
  }

  const catalogue = await load();
  return { view, query, catalogue };
}

async function startCollectionInterface({ document, auth, ratings, collection }) {
  const [catalogue, availability] = await Promise.all([
    loadCatalogue(),
    loadAvailability({ document }).catch(() => new Map()),
  ]);
  const view = createCatalogueView({
    document,
    pageSize: Number.MAX_SAFE_INTEGER,
    sectionRoots: {
      current: document.querySelector("#collectionCurrentResults"),
      past: document.querySelector("#collectionPastResults"),
    },
    onMount(root) {
      ratings.mount(root).catch(() => {});
      collection.mount(root);
    },
  });
  view.setAvailability(availability);
  const query = bindQueryControls({
    document,
    onChange: (next) => view.setQuery(next),
  });
  const page = createCollectionPage({
    document,
    auth,
    collection,
    catalogue: indexCatalogue(catalogue),
    view,
  });
  auth.subscribe((state) => page.setAuthState(state));
  await page.start();
  return { view, query, page, catalogue };
}

export async function startAccountInterface(document = globalThis.document) {
  const menu = createBurgerMenu({ document });
  const auth = createAuthController({ accountContainer: menu.userButtonSlot });
  const feedback = createFeedbackController({ document });
  let ratings;
  let collection;
  const reverseAction = {
    add: "remove-current",
    finish: "restore",
    restore: "finish",
    "remove-current": "add",
  };
  const collectionMessage = ({ code, action }) => ({
    add: `Added ${code} to Current Collection.`,
    finish: `Moved ${code} to Past Bottles.`,
    restore: `Restored ${code} to Current Collection.`,
    "remove-current": `Removed one ${code} from Current Collection.`,
    "remove-past": `Removed one ${code} from Past Bottles.`,
  })[action];
  const onCollectionAction = (result) => {
    const reverse = reverseAction[result.action];
    feedback.announce(collectionMessage(result), {
      undo: reverse ? async () => {
        const undone = await collection.act(result.code, reverse, { silent: true });
        feedback.announce(undone ? `Undid the change to ${result.code}.` : `Undo for ${result.code} could not be completed.`);
      } : null,
    });
  };
  const onRatingSave = (result) => {
    feedback.announce(`Rated ${result.code} ${result.summary.yourRating}/10.`, {
      undo: result.previousRating == null ? null : async () => {
        try {
          await ratings.setRating(result.code, result.previousRating, { silent: true });
          feedback.announce(`Restored ${result.code} to ${result.previousRating}/10.`);
        } catch {
          feedback.announce(`Undo for ${result.code} could not be completed.`);
        }
      },
    });
  };
  ratings = createRatingsController({ document, auth, onSave: onRatingSave });
  collection = createCollectionController({ document, auth, onAction: onCollectionAction });
  menu.setSignInHandler(() => auth.requireUser());
  auth.subscribe(({ ready, signedIn, unavailable }) => menu.setAuthState({
    ready,
    signedIn,
    unavailable,
  }));
  const ratingsStarted = ratings.start().catch(() => {});
  const collectionStarted = collection.start().catch(() => {});
  const pageStarted = document.body.dataset.page === "browse"
    ? startBrowseInterface({ document, ratings, collection })
    : startCollectionInterface({ document, auth, ratings, collection }).catch(() => null);
  try {
    await auth.start();
  } catch {
    menu.setAuthUnavailable();
    await auth.setUnavailable?.();
  }
  const [, , page] = await Promise.all([ratingsStarted, collectionStarted, pageStarted]);
  return { auth, menu, ratings, collection, feedback, page };
}

export function startAccountInterfaceWhenReady(document = globalThis.document) {
  const start = () => startAccountInterface(document).catch(() => {});
  if (document.readyState === "complete") {
    start();
    return;
  }
  document.addEventListener("DOMContentLoaded", start, { once: true });
}

if (typeof document !== "undefined") {
  startAccountInterfaceWhenReady();
}
